import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Briefcase, Wallet, TrendingUp, Package, ShieldCheck, ArrowRight } from "lucide-react";
import { MarketingLayout, Section } from "@/components/marketing/MarketingLayout";
import { IMG, inr } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const TYPES = ["Contractor", "Builder", "Developer", "Architect", "Interior Designer", "Engineer", "Construction Company", "Supplier", "Vendor"];
const METRICS = [["24", "Total Projects"], ["18", "Active Sites"], [inr(840000000), "Project Value"], ["76%", "Avg Progress"]];
const FEATURES = [
  { icon: Users, t: "CRM & Leads", d: "Pipeline from enquiry to project" },
  { icon: Briefcase, t: "Estimation & BOQ", d: "Data-driven, versioned, INR-native" },
  { icon: Package, t: "Procurement", d: "PR → PO → GRN → Bill → Payment" },
  { icon: Wallet, t: "Finance", d: "Cashflow, invoicing, outstanding" },
  { icon: ShieldCheck, t: "Quality & Snagging", d: "IS-code aware inspections" },
  { icon: TrendingUp, t: "AI Copilot", d: "Which projects are at risk?" },
];

export default function Business() {
  const nav = useNavigate();
  return (
    <MarketingLayout>
      <section className="bg-sp-navy text-white relative overflow-hidden">
        <img src={IMG.highrise} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative sp-container py-24 lg:py-32 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="sp-eyebrow mb-4">Sitepar Business</div>
            <h1 className="text-4xl lg:text-6xl font-bold tracking-tight leading-tight">Run your construction business from one operating system.</h1>
            <p className="mt-6 text-lg text-white/65 max-w-lg">Projects, teams, clients, materials and finance — unified. Built for Indian contractors, builders and developers.</p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Button data-testid="business-signup" onClick={() => nav("/register?type=business")} className="bg-sp-orange hover:bg-sp-orangedark rounded-full h-12 px-7">Start Free <ArrowRight className="ml-2 h-4 w-4" /></Button>
              <Button data-testid="business-demo" variant="outline" onClick={() => nav("/pricing")} className="rounded-full h-12 px-7 bg-transparent border-white/25 text-white hover:bg-white/10">See Pricing</Button>
            </div>
          </div>
          <div className="sp-glass rounded-xl p-5">
            <div className="text-xs text-white/55 mb-3">Good morning, Apex Buildworks</div>
            <div className="grid grid-cols-2 gap-3">
              {METRICS.map(([v, l]) => (
                <div key={l} className="rounded-lg bg-white/5 p-3 border border-white/10">
                  <div className="text-xl font-bold text-sp-orange">{v}</div>
                  <div className="text-[11px] text-white/55 mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="sp-eyebrow mb-3">For every kind of builder</div>
        <h2 className="text-3xl lg:text-4xl font-bold text-sp-navy tracking-tight">One platform. Every business type.</h2>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {TYPES.map((t) => (
            <span key={t} className="px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-sp-orange hover:text-sp-orange transition-colors">{t}</span>
          ))}
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.t} className="rounded-lg border border-slate-200 p-6 sp-hover-card bg-white">
              <div className="h-11 w-11 rounded-lg bg-sp-navy flex items-center justify-center mb-4"><f.icon className="h-5 w-5 text-sp-orange" /></div>
              <div className="font-semibold text-sp-navy">{f.t}</div>
              <div className="text-sm text-slate-500 mt-1">{f.d}</div>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-sp-orange text-white">
        <div className="sp-container py-16 flex flex-col sm:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl lg:text-3xl font-bold">Create your organization in minutes.</h2>
          <Button data-testid="business-cta" onClick={() => nav("/register?type=business")} className="bg-white text-sp-navy hover:bg-white/90 rounded-full h-12 px-7">Get Started Free</Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
